import * as React from 'react';
import { 
  Typography, 
  TextField, 
  FormControl, 
  Stack,
  Paper,
  Autocomplete,
  Chip,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchTags } from '@/store/slices/tagsSlice';
import type { WorkflowDetailsCardProps } from '@/types';

export default function WorkflowDetailsCard({ 
  title, 
  setTitle, 
  selectedTags, 
  setSelectedTags, 
  titleError, 
  tagsError 
}: WorkflowDetailsCardProps) {
  const [searchTerm, setSearchTerm] = React.useState('');

  const dispatch = useAppDispatch();
  const { items: availableTags, loading, error } = useAppSelector((state) => state.tags) as {
    items: Array<{ id: number; name: string }>;
    loading: boolean;
    error: string | null;
  };

  // Load tags on component mount
  React.useEffect(() => {
    dispatch(fetchTags());
  }, [dispatch]);

  // Load tags when search term changes (with debounce)
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        dispatch(fetchTags(searchTerm.trim()));
      } else {
        dispatch(fetchTags());
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, dispatch]);

  const handleTagChange = (_event: React.SyntheticEvent, newValue: string[]) => {
    setSelectedTags(newValue);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  return (
    <Paper sx={{ 
      padding: 4,
      borderRadius: 2,
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
      maxWidth: 600
    }}>
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
          fontWeight: 'bold',
          marginBottom: 4,
          fontSize: '1.5rem'
        }}
      >
        Workflow Details
      </Typography>

      <Stack spacing={4}>
        {/* Title Field */}
        <FormControl fullWidth error={!!titleError}>
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            label="Title"
            variant="outlined"
            error={!!titleError}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: titleError ? 'error.main' : 'divider',
                borderWidth: 1,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: titleError ? 'error.main' : 'action.hover',
                borderWidth: 1,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: titleError ? 'error.main' : 'primary.main',
                borderWidth: 1,
              },
              '& input': {
                padding: '16px 14px',
                fontSize: '1rem'
              }
            }}
          />
          {titleError && (
            <FormHelperText sx={{ marginTop: 1 }}>
              {titleError}
            </FormHelperText>
          )}
        </FormControl>

        {/* Tags Field */}
        <FormControl fullWidth error={!!tagsError}>
          <Autocomplete
            multiple
            options={availableTags.map((tag: { name: string }) => tag.name)}
            value={selectedTags}
            onChange={handleTagChange}
            onInputChange={(_event, newInputValue) => {
              setSearchTerm(newInputValue);
            }}
            loading={loading}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                  onDelete={() => handleRemoveTag(option)}
                  sx={{
                    borderColor: 'divider',
                    backgroundColor: 'background.elevation1',
                    fontSize: '0.875rem',
                    height: '28px',
                    margin: '4px 4px 4px 0',
                    '& .MuiChip-deleteIcon': {
                      color: 'text.secondary',
                      fontSize: '18px',
                      '&:hover': {
                        color: 'text.primary'
                      }
                    },
                    '& .MuiChip-label': {
                      padding: '0 8px 0 8px'
                    }
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                error={!!tagsError}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: '56px',
                    padding: '8px 14px',
                    '& fieldset': {
                      borderColor: tagsError ? 'error.main' : 'divider',
                      borderWidth: 1,
                    },
                    '&:hover fieldset': {
                      borderColor: tagsError ? 'error.main' : 'action.hover',
                      borderWidth: 1,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: tagsError ? 'error.main' : 'primary.main',
                      borderWidth: 1,
                    },
                    '& input': {
                      padding: '8px 0',
                      fontSize: '1rem',
                      minWidth: '120px'
                    },
                    '& .MuiAutocomplete-input': {
                      padding: '0 !important'
                    }
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option}>
                {option}
              </li>
            )}
            noOptionsText={error ? `Error: ${error}` : "No tags found"}
            sx={{ width: '100%' }}
          />
          {tagsError && (
            <FormHelperText sx={{ marginTop: 1 }}>
              {tagsError}
            </FormHelperText>
          )}
        </FormControl>
      </Stack>
    </Paper>
  );
}
